import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NumericFormat } from 'react-number-format';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Trash2, Upload, X, FileIcon, Paperclip } from 'lucide-react';
import { cn, formatCurrency } from '../../lib/utils';
import { vouchersApi, departmentsApi, usersApi } from '../../lib/api';
import { toast } from 'sonner';

// UUID generator that works in non-secure contexts (HTTP)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (e) {
      // Fall through to fallback
    }
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Convert number to words with currency
const numberToWords = (num, currency = 'SAR') => {
  const currencyInfo = {
    'SAR': { name: 'Saudi Riyals', subunit: 'Halalas', singular: 'Saudi Riyal', subunitSingular: 'Halala' },
    'AED': { name: 'UAE Dirhams', subunit: 'Fils', singular: 'UAE Dirham', subunitSingular: 'Fil' },
    'USD': { name: 'US Dollars', subunit: 'Cents', singular: 'US Dollar', subunitSingular: 'Cent' },
    'EUR': { name: 'Euros', subunit: 'Cents', singular: 'Euro', subunitSingular: 'Cent' },
    'GBP': { name: 'British Pounds', subunit: 'Pence', singular: 'British Pound', subunitSingular: 'Penny' },
  };

  const info = currencyInfo[currency] || { name: currency, subunit: 'Cents', singular: currency, subunitSingular: 'Cent' };

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertBelowHundred = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? '-' + ones[n % 10] : '');
  };

  const convertBelowThousand = (n) => {
    if (n < 100) return convertBelowHundred(n);
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertBelowHundred(n % 100) : '');
  };

  const convert = (n) => {
    if (n === 0) return 'Zero';
    if (n < 1000) return convertBelowThousand(n);
    if (n < 1000000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 !== 0 ? ' ' + convertBelowThousand(n % 1000) : '');
    if (n < 1000000000) return convert(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 !== 0 ? ' ' + convert(n % 1000000) : '');
    return convert(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 !== 0 ? ' ' + convert(n % 1000000000) : '');
  };

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  const currencyName = integerPart === 1 ? info.singular : info.name;

  if (integerPart === 0 && decimalPart > 0) {
    const subunitName = decimalPart === 1 ? info.subunitSingular : info.subunit;
    return `${convertBelowHundred(decimalPart)} ${subunitName} Only`;
  } else if (decimalPart > 0) {
    const subunitName = decimalPart === 1 ? info.subunitSingular : info.subunit;
    return `${convert(integerPart)} ${currencyName} and ${convertBelowHundred(decimalPart)} ${subunitName} Only`;
  } else {
    return `${convert(integerPart)} ${currencyName} Only`;
  }
};

const DEPARTMENTS = [
  'Administrative Assistant',
  'Business Development',
  'Contracts',
  'Digital OPS & Traffic',
  'Finance',
  'Human Resource',
  'Information Technology',
  'Internal Audit',
  'Investor Relations',
  'IR & Governance',
  'Kitchen',
  'Maintenance',
  'Management',
  'Marketing',
  'Project Management',
  'Purchasing',
  'Reception',
  'Sales',
];

const BRANCHES = [
  'Boulevard',
  'Head Office',
  'Jeddah',
  'Madeena',
  'Murooj Office',
  'Warehouse',
];

const itemSchema = z.object({
  id: z.string().optional(),
  invoiceNo: z.string().nullable().optional(),
  amount: z.any().optional(),  // Invoice Amount - optional, any value accepted
  payableAmount: z.preprocess(
    // Preprocess to convert string/number to number, handle NaN
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = typeof val === 'number' ? val : parseFloat(String(val));
      return isNaN(num) ? undefined : num;
    },
    z.number({ required_error: 'Payable Amount is required' })
      .positive('Payable Amount must be greater than 0')
  ),
  currency: z.string().default('SAR'),
  note: z.string().nullable().optional(),
});

const voucherSchema = z.object({
  date: z.string().min(1, 'Date is required'),
  branch: z.string().min(1, 'Branch is required'),
  department: z.string().min(1, 'Department is required'),
  beneficiaryName: z.string().min(1, 'Beneficiary name is required'),
  beneficiaryAddress: z.preprocess(val => val === null ? '' : val, z.string().optional()),
  paymentMethod: z.enum(['CASH', 'CHECK', 'TRANSFER', 'SADAD']),
  currency: z.string().default('SAR'),
  amount: z.any().optional(),
  poAmount: z.any().optional(),
  bankName: z.preprocess(val => val === null ? '' : val, z.string().optional()),
  iban: z.preprocess(val => val === null ? '' : val, z.string().optional()),
  paymentType: z.enum(['ADVANCE', 'ON_ACCOUNT', 'BALANCE']),
  advancePercent: z.any().optional(),
  balancePercent: z.any().optional(),
  poNumber: z.preprocess(val => val === null ? '' : val, z.string().optional()),
  paymentRefNumber: z.preprocess(val => val === null ? '' : val, z.string().optional()),
  items: z.array(itemSchema).min(1, 'At least one item is required'),
});

export const VoucherForm = ({ voucher, onSubmit, onCancel, isLoading, isEditing = false }) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentManagers, setDepartmentManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(voucher?.assignedManagerId || '');
  const isEdit = !!voucher;
  // Use isEditing prop to determine button text - if explicitly editing, show "Update"
  const showUpdateButton = isEditing || (voucher && voucher.id);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const deptsData = await departmentsApi.getAll();
        setDepartments(deptsData);
      } catch (error) {
        console.error('Failed to fetch departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(voucherSchema),
    mode: 'onSubmit',
    shouldFocusError: false,  // Prevent auto-focus on error fields
    defaultValues: voucher ? {
      ...voucher,
      amount: voucher.amountTotal || '',
      poAmount: voucher.poAmount || '',
      items: (voucher.items || []).map(item => ({
        ...item,
        // Handle null/undefined amounts properly for duplicated vouchers
        amount: item.amount != null ? item.amount : '',
        payableAmount: item.payableAmount != null ? item.payableAmount : '',
        currency: item.currency || voucher.currency || 'SAR',
      })),
    } : {
      date: format(new Date(), 'yyyy-MM-dd'),
      branch: 'Head Office',
      department: '',
      beneficiaryName: '',
      beneficiaryAddress: '',
      paymentMethod: 'CASH',
      currency: 'SAR',
      amount: '',
      poAmount: '',
      bankName: '',
      iban: '',
      paymentType: 'BALANCE',
      advancePercent: null,
      balancePercent: null,
      poNumber: '',
      paymentRefNumber: '',
      items: [{ invoiceNo: '', amount: '', payableAmount: '', currency: 'SAR', note: '' }],
    },
  });

  // State for formatted amount display
  const [formattedAmount, setFormattedAmount] = useState(() => {
    const initialAmount = voucher?.amountTotal;
    return initialAmount ? initialAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  });

  // Format number with thousands separator and 2 decimal places
  const formatWithCommas = (value) => {
    if (!value && value !== 0) return '';
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle amount input change
  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, '');
    setFormattedAmount(e.target.value); // Keep what user types
    if (rawValue === '' || rawValue === '.') {
      setValue('amount', '');
      return;
    }
    const numValue = parseFloat(rawValue);
    if (!isNaN(numValue)) {
      setValue('amount', numValue);
    }
  };

  // Format on blur (when user leaves the field)
  const handleAmountBlur = (e) => {
    const rawValue = e.target.value.replace(/,/g, '');
    if (rawValue === '') return;
    const numValue = parseFloat(rawValue);
    if (!isNaN(numValue)) {
      setFormattedAmount(formatWithCommas(numValue));
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchPaymentMethod = watch('paymentMethod');
  const watchItems = watch('items');
  const watchPaymentType = watch('paymentType');
  const watchDepartment = watch('department');
  const watchCurrency = watch('currency');
  const watchAmount = watch('amount');

  // Sync currency from Payment Details to Line Items when currency changes
  useEffect(() => {
    if (watchCurrency && watchItems) {
      watchItems.forEach((_, index) => {
        setValue(`items.${index}.currency`, watchCurrency);
      });
    }
  }, [watchCurrency, setValue]);

  // Fetch managers when department changes
  useEffect(() => {
    const fetchManagersForDepartment = async () => {
      if (!watchDepartment) {
        setDepartmentManagers([]);
        setSelectedManager('');
        return;
      }
      
      try {
        const managers = await departmentsApi.getManagersByDeptName(watchDepartment);
        setDepartmentManagers(managers);
        
        // Auto-select manager if there's only one manager for this department
        // Use setTimeout to ensure the Select options are rendered first
        if (managers.length === 1) {
          setTimeout(() => {
            setSelectedManager(managers[0].id);
            setManagerError('');
          }, 100);
        } else if (selectedManager && !managers.find(m => m.id === selectedManager)) {
          // Reset selected manager if not in the new department's managers
          setSelectedManager('');
        }
      } catch (error) {
        console.error('Failed to fetch department managers:', error);
        setDepartmentManagers([]);
      }
    };
    
    fetchManagersForDepartment();
  }, [watchDepartment]);

  useEffect(() => {
    if (voucher?.attachments) {
      setAttachments(voucher.attachments.map(a => {
        const [filename, originalName] = a.split('|');
        return { filename, originalName: originalName || filename, isUploaded: true };
      }));
    }
  }, [voucher]);

  // Total amount now comes from sum of all Payable Amount values in line items
  const totalPayableAmount = watchItems?.reduce((sum, item) => sum + (Number(item.payableAmount) || 0), 0) || 0;

  // Maximum file size: 5MB
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  // Handle file selection for new voucher
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map(f => f.name).join(', ');
      toast.error(`File(s) exceed 5MB limit: ${fileNames}`);
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    const newPendingFiles = validFiles.map(file => ({
      file,
      id: generateUUID(),
      name: file.name,
      size: file.size,
    }));

    setPendingFiles(prev => [...prev, ...newPendingFiles]);
    e.target.value = ''; // Reset input
  };

  // Handle file upload for existing voucher
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !voucher?.id) return;

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File "${file.name}" exceeds 5MB limit. Please select a smaller file.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const result = await vouchersApi.uploadAttachment(voucher.id, file);
      setAttachments(prev => [...prev, { filename: result.filename, originalName: result.originalName, isUploaded: true }]);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Remove pending file (not yet uploaded)
  const handleRemovePendingFile = (fileId) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Remove uploaded attachment
  const handleRemoveAttachment = async (filename) => {
    if (!voucher?.id) return;
    try {
      await vouchersApi.deleteAttachment(voucher.id, filename);
      setAttachments(prev => prev.filter(a => a.filename !== filename));
      toast.success('Attachment removed');
    } catch (error) {
      toast.error('Failed to remove attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const [managerError, setManagerError] = useState('');

  const onFormSubmit = (data) => {
    console.log('=== FORM SUBMIT TRIGGERED ===');
    console.log('Raw form data:', JSON.stringify(data, null, 2));
    console.log('Items payableAmount values:', data.items.map(i => ({ raw: i.payableAmount, type: typeof i.payableAmount })));
    
    // Manager selection is mandatory
    if (!selectedManager) {
      setManagerError('Approving Manager is required');
      console.log('ERROR: No manager selected');
      return;
    }
    setManagerError('');
    
    // Calculate amountTotal from sum of all Payable Amount values
    const amountTotalValue = data.items.reduce((sum, item) => sum + (Number(item.payableAmount) || 0), 0);
    console.log('Calculated amountTotal:', amountTotalValue);
    
    const processedData = {
      ...data,
      items: data.items.map(item => ({
        ...item,
        // Invoice Amount is optional - send null if not provided or NaN
        amount: (item.amount !== undefined && item.amount !== null && !isNaN(item.amount)) ? Number(item.amount) : null,
        payableAmount: Number(item.payableAmount) || 0,
        currency: data.currency, // Use payment details currency
        id: item.id || generateUUID(),
      })),
      amountTotal: amountTotalValue, // Use sum of Payable Amounts
      poAmount: (data.poAmount !== '' && data.poAmount !== null && data.poAmount !== undefined && !isNaN(Number(data.poAmount))) ? Number(data.poAmount) : null,
      advancePercent: (data.advancePercent !== '' && data.advancePercent !== null && data.advancePercent !== undefined && !isNaN(Number(data.advancePercent))) ? Number(data.advancePercent) : null,
      balancePercent: (data.balancePercent !== '' && data.balancePercent !== null && data.balancePercent !== undefined && !isNaN(Number(data.balancePercent))) ? Number(data.balancePercent) : null,
      pendingFiles: pendingFiles.map(f => f.file), // Pass pending files to parent
      assignedManagerId: selectedManager,
    };
    
    console.log('Processed data to submit:', JSON.stringify(processedData, null, 2));
    onSubmit(processedData);
  };
  
  // Handle form validation errors
  const onFormError = (errors) => {
    console.error('Form validation errors:', errors);
    // Show specific field errors
    const errorFields = Object.keys(errors);
    const errorMessages = errorFields.map(field => {
      if (field === 'items' && errors.items) {
        // Handle nested item errors
        const itemErrors = Object.keys(errors.items).map(idx => {
          const itemErr = errors.items[idx];
          return Object.keys(itemErr).map(f => `Item ${parseInt(idx)+1} ${f}: ${itemErr[f]?.message}`).join(', ');
        }).join('; ');
        return itemErrors;
      }
      return `${field}: ${errors[field]?.message || 'invalid'}`;
    }).join(', ');
    console.error('Error details:', errorMessages);
    toast.error(`Validation errors: ${errorMessages}`);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit, onFormError)} className="space-y-6" data-testid="voucher-form">
      {/* Basic Information */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-heading">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date */}
          <div className="space-y-2">
            <Label htmlFor="date">Date *</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !watch('date') && "text-muted-foreground"
                  )}
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {watch('date') ? format(new Date(watch('date')), 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={watch('date') ? new Date(watch('date')) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setValue('date', format(date, 'yyyy-MM-dd'));
                      setDateOpen(false);
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {errors.date && <p className="text-xs text-destructive">{errors.date.message}</p>}
          </div>

          {/* Branch */}
          <div className="space-y-2">
            <Label htmlFor="branch">Branch *</Label>
            <Select
              value={watch('branch')}
              onValueChange={(value) => setValue('branch', value)}
            >
              <SelectTrigger data-testid="branch-select">
                <SelectValue placeholder="Select branch" />
              </SelectTrigger>
              <SelectContent>
                {BRANCHES.map((branch) => (
                  <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.branch && <p className="text-xs text-destructive">{errors.branch.message}</p>}
          </div>

          {/* Department */}
          <div className="space-y-2">
            <Label htmlFor="department">Department *</Label>
            <Select
              value={watch('department')}
              onValueChange={(value) => setValue('department', value)}
            >
              <SelectTrigger data-testid="department-select">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {departments.length > 0 
                  ? departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                    ))
                  : DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))
                }
              </SelectContent>
            </Select>
            {errors.department && <p className="text-xs text-destructive">{errors.department.message}</p>}
          </div>

          {/* Approving Manager */}
          <div className="space-y-2">
            <Label htmlFor="manager">Approving Manager *</Label>
            <Select
              value={selectedManager}
              onValueChange={(value) => {
                setSelectedManager(value);
                setManagerError('');
              }}
              disabled={!watchDepartment || departmentManagers.length === 0}
            >
              <SelectTrigger data-testid="manager-select" className={managerError ? 'border-destructive' : ''}>
                <SelectValue placeholder={
                  !watchDepartment 
                    ? "Select department first" 
                    : departmentManagers.length === 0 
                      ? "No managers assigned to this department"
                      : "Select manager to approve"
                } />
              </SelectTrigger>
              <SelectContent>
                {departmentManagers.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    {manager.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {managerError && <p className="text-xs text-destructive">{managerError}</p>}
          </div>

          {/* Beneficiary Name - Half Width */}
          <div className="space-y-2 lg:col-span-2 md:col-span-1">
            <Label htmlFor="beneficiaryName">Beneficiary Name *</Label>
            <Input
              {...register('beneficiaryName')}
              placeholder="Enter beneficiary name"
              data-testid="beneficiary-name-input"
            />
            {errors.beneficiaryName && <p className="text-xs text-destructive">{errors.beneficiaryName.message}</p>}
          </div>

          {/* Beneficiary Address - Half Width */}
          <div className="space-y-2 lg:col-span-2 md:col-span-1">
            <Label htmlFor="beneficiaryAddress">Beneficiary Address</Label>
            <Input
              {...register('beneficiaryAddress')}
              placeholder="Enter beneficiary address"
              data-testid="beneficiary-address-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment Details */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-heading">Payment Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select
                value={watch('paymentMethod')}
                onValueChange={(value) => setValue('paymentMethod', value)}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="CHECK">Cheque</SelectItem>
                  <SelectItem value="TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="SADAD">SADAD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Type */}
            <div className="space-y-2">
              <Label>Payment Type *</Label>
              <Select
                value={watch('paymentType')}
                onValueChange={(value) => setValue('paymentType', value)}
              >
                <SelectTrigger data-testid="payment-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADVANCE">Advance Payment</SelectItem>
                  <SelectItem value="ON_ACCOUNT">On Account</SelectItem>
                  <SelectItem value="BALANCE">Balance Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={watch('currency')}
                onValueChange={(value) => setValue('currency', value)}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SAR">SAR</SelectItem>
                  <SelectItem value="AED">AED (Dirham)</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* PO Amount - Optional */}
            <div className="space-y-2">
              <Label htmlFor="poAmount">PO Amount</Label>
              <Controller
                name="poAmount"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <NumericFormat
                    value={value || ''}
                    onValueChange={(values) => {
                      onChange(values.floatValue ?? '');
                    }}
                    thousandSeparator=","
                    decimalScale={2}
                    allowNegative={false}
                    placeholder="Optional"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="po-amount-input"
                    getInputRef={ref}
                  />
                )}
              />
            </div>
          </div>

          {/* Bank Details (for Transfer) */}
          {watchPaymentMethod === 'TRANSFER' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name</Label>
                <Input
                  {...register('bankName')}
                  placeholder="Enter bank name (optional)"
                  data-testid="bank-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  {...register('iban')}
                  placeholder="Enter IBAN (optional)"
                  data-testid="iban-input"
                />
              </div>
            </div>
          )}

          {/* Reference Numbers and Advance/Balance Percent - Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="poNumber">PO Number</Label>
              <Input
                {...register('poNumber')}
                placeholder="Optional"
                data-testid="po-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paymentRefNumber">No of Payment</Label>
              <Input
                {...register('paymentRefNumber')}
                placeholder="Optional"
                data-testid="payment-ref-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="advancePercent">Advance % (Optional)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                {...register('advancePercent', { 
                  setValueAs: (v) => v === '' || v === null || v === undefined ? null : Number(v),
                  max: { value: 100, message: 'Maximum value is 100%' },
                  min: { value: 0, message: 'Minimum value is 0%' }
                })}
                placeholder="e.g., 30"
                data-testid="advance-percent-input"
              />
              {errors.advancePercent && <p className="text-xs text-destructive">{errors.advancePercent.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="balancePercent">Balance % (Optional)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                {...register('balancePercent', { 
                  setValueAs: (v) => v === '' || v === null || v === undefined ? null : Number(v),
                  max: { value: 100, message: 'Maximum value is 100%' },
                  min: { value: 0, message: 'Minimum value is 0%' }
                })}
                placeholder="e.g., 70"
                data-testid="balance-percent-input"
              />
              {errors.balancePercent && <p className="text-xs text-destructive">{errors.balancePercent.message}</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading">Line Items</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ invoiceNo: '', amount: '', payableAmount: '', currency: watchCurrency, note: '' })}
            data-testid="add-item-button"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Header */}
            <div className="hidden md:grid md:grid-cols-12 gap-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <div className="col-span-2">Document No.</div>
              <div className="col-span-2">Invoice Amount</div>
              <div className="col-span-2">Payable Amount *</div>
              <div className="col-span-1">Currency</div>
              <div className="col-span-4">Description</div>
              <div className="col-span-1"></div>
            </div>

            {/* Items */}
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100"
                data-testid={`item-row-${index}`}
              >
                <div className="md:col-span-2">
                  <Label className="md:hidden text-xs mb-1">Document No.</Label>
                  <Input
                    {...register(`items.${index}.invoiceNo`)}
                    placeholder="DOC-001"
                    data-testid={`item-invoice-${index}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="md:hidden text-xs mb-1">Invoice Amount</Label>
                  <Controller
                    name={`items.${index}.amount`}
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <NumericFormat
                        value={value || ''}
                        onValueChange={(values) => {
                          onChange(values.floatValue ?? '');
                        }}
                        thousandSeparator=","
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="Optional"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`item-amount-${index}`}
                        getInputRef={ref}
                      />
                    )}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="md:hidden text-xs mb-1">Payable Amount *</Label>
                  <Controller
                    name={`items.${index}.payableAmount`}
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <NumericFormat
                        value={value || ''}
                        onValueChange={(values) => {
                          onChange(values.floatValue ?? '');
                        }}
                        thousandSeparator=","
                        decimalScale={2}
                        allowNegative={false}
                        placeholder="Required"
                        className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.items?.[index]?.payableAmount ? 'border-red-500' : ''}`}
                        data-testid={`item-payable-${index}`}
                        getInputRef={ref}
                      />
                    )}
                  />
                  {errors.items?.[index]?.payableAmount && (
                    <p className="text-xs text-destructive mt-1">{errors.items[index].payableAmount.message}</p>
                  )}
                </div>
                <div className="md:col-span-1">
                  <Label className="md:hidden text-xs mb-1">Currency</Label>
                  <Input
                    value={watchCurrency}
                    readOnly
                    className="bg-gray-100 cursor-not-allowed text-center"
                    data-testid={`item-currency-${index}`}
                  />
                  <input type="hidden" {...register(`items.${index}.currency`)} value={watchCurrency} />
                </div>
                <div className="md:col-span-4">
                  <Label className="md:hidden text-xs mb-1">Description</Label>
                  <Input
                    {...register(`items.${index}.note`)}
                    placeholder="Description"
                    data-testid={`item-note-${index}`}
                  />
                </div>
                <div className="md:col-span-1 flex items-end justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => fields.length > 1 && remove(index)}
                    disabled={fields.length === 1}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}

            {errors.items && (
              <p className="text-xs text-destructive">{errors.items.message || errors.items.root?.message}</p>
            )}

            {/* Total */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Payable Amount</p>
                <p className="text-2xl font-mono font-semibold text-[#6a5da4]" data-testid="total-amount">
                  {formatCurrency(totalPayableAmount, watch('currency'))}
                </p>
                {totalPayableAmount > 0 && (
                  <p className="text-sm text-muted-foreground mt-2 italic" data-testid="amount-in-words">
                    {numberToWords(totalPayableAmount, watch('currency'))}
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attachments - Available for both create and edit */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Paperclip className="w-5 h-5" />
            Attachments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload Button */}
            <div className="flex items-center gap-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  onChange={isEdit ? handleFileUpload : handleFileSelect}
                  disabled={uploading}
                  multiple={!isEdit}
                  data-testid="file-upload-input"
                />
                <div className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#6a5da4] hover:bg-purple-50 transition-colors">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      {uploading ? 'Uploading...' : 'Click to upload files'}
                    </span>
                    <p className="text-xs text-muted-foreground">
                      PDF, Images, Documents (Max 5MB each)
                    </p>
                  </div>
                </div>
              </label>
            </div>

            {/* Pending Files (for new voucher) */}
            {!isEdit && pendingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Files to upload ({pendingFiles.length})</p>
                {pendingFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"
                    data-testid={`pending-file-${file.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileIcon className="w-5 h-5 text-blue-600" />
                      <div>
                        <span className="text-sm font-medium text-gray-800">{file.name}</span>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePendingFile(file.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded Attachments (for edit mode) */}
            {isEdit && attachments.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Uploaded files ({attachments.length})</p>
                {attachments.map((attachment) => (
                  <div
                    key={attachment.filename}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    data-testid={`attachment-${attachment.filename}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileIcon className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm">{attachment.originalName}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAttachment(attachment.filename)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {((isEdit && attachments.length === 0) || (!isEdit && pendingFiles.length === 0)) && (
              <p className="text-sm text-muted-foreground text-center py-2">
                No files attached yet
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          data-testid="cancel-button"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-[#6a5da4] hover:bg-[#5a4d94]"
          data-testid="save-button"
        >
          {isLoading ? 'Saving...' : showUpdateButton ? 'Update Voucher' : 'Create Voucher'}
        </Button>
      </div>
    </form>
  );
};

export default VoucherForm;
